import React, { useState } from 'react';

import {
  ActionsContainer,
  CheckIcon,
  Container,
  Date,
  DateAndTime,
  HeaderMobile,
  LisItemMobile,
  ListHeader,
  ListItem,
  ListItemInfo,
  OverflowMenuButton,
  overflowMenuClassName,
  OverflowMenuStyles,
  QuestionIcon,
  Time,
  TodaysBadge
} from './List.styled';
import { Calendar, Clock, Ellipsis, Pin } from 'assets/icons/Icons';
import { Initials } from 'components/Initials/Initials';
import { Button } from 'components/v2/Buttons/Button/Button';
import { GenericDropdown } from 'components/v2/DropdownMenu/Dropdown';
import { MenuItemDetailsArray } from 'components/v2/DropdownMenu/Dropdown.types';
import { Text } from 'components/v2/Typography/Text';
import { getTimeZone } from 'hooks/getTimeZone';
import { Appointment } from 'kb-shared';
import { shouldDisplayProvider } from 'kb-shared/utilities/appointment.helper';
import { DEFAULT_DATE_FORMAT, HOUR_WITH_TIMEZONE } from 'utilities/formatDate';
import getAppointmentChannel from 'utilities/getAppointmentChannel';
import { useBreakpoints } from 'utilities/useBreakpoints';

import { AppointmentDetailModal } from '../AppointmentDetailModal/AppointmentDetailModal';

export const List = ({
  appointments,
  onRescheduledClick,
  onCancelClick,
  highlightTodaysListItems
}: {
  appointments: Appointment[];
  onRescheduledClick?: (appointment: Appointment) => void;
  onCancelClick?: (appointment: Appointment) => void;
  highlightTodaysListItems?: boolean;
}) => {
  const { isMobile, isTablet } = useBreakpoints();
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);

  const isUpcoming = !!onRescheduledClick;

  const getVideoLink = (appointment: Appointment) =>
    appointment.videoLink ?? appointment.appointmentType.videoLink;

  const handleJoinVideoConsult = (appointment: Appointment) => {
    const videoLink = getVideoLink(appointment);
    if (videoLink) {
      window.open(videoLink, '_blank');
    }
  };

  const getMenuItems = (appointment: Appointment): MenuItemDetailsArray => {
    const { isVideoCall } = getAppointmentChannel(appointment.appointmentType);

    if (!isUpcoming) return [];

    if (isVideoCall) {
      // Video consult: Reschedule, View Details
      return [
        {
          label: 'View Details',
          type: 'default',
          onClick: () => setSelectedAppointment(appointment)
        },
        {
          label: 'Reschedule',
          type: 'warning',
          onClick: () => onRescheduledClick?.(appointment)
        }
      ];
    }

    return [
      {
        label: 'View Details',
        type: 'default',
        onClick: () => setSelectedAppointment(appointment)
      },
      ...(onCancelClick
        ? [
            {
              label: 'Cancel Appointment',
              type: 'warning' as const,
              onClick: () => onCancelClick(appointment)
            }
          ]
        : [])
    ];
  };

  const renderActions = (appointment: Appointment) => {
    const { isVideoCall } = getAppointmentChannel(appointment.appointmentType);
    const menuItems = getMenuItems(appointment);
    const hasMenu = menuItems.length > 0;

    if (!isUpcoming) {
      return (
        <Button
          label="View Details"
          category="secondary"
          onClick={() => setSelectedAppointment(appointment)}
        />
      );
    }

    if (isVideoCall) {
      return (
        <>
          {hasMenu && (
            <OverflowMenuStyles>
              <GenericDropdown className={overflowMenuClassName} items={menuItems}>
                <OverflowMenuButton type="button" aria-label="More options">
                  <Ellipsis />
                </OverflowMenuButton>
              </GenericDropdown>
            </OverflowMenuStyles>
          )}
          <Button
            label="Join Video Consult"
            category="primary"
            onClick={() => handleJoinVideoConsult(appointment)}
          />
        </>
      );
    }

    return (
      <>
        {hasMenu && (
          <OverflowMenuStyles>
            <GenericDropdown className={overflowMenuClassName} items={menuItems}>
              <OverflowMenuButton type="button" aria-label="More options">
                <Ellipsis />
              </OverflowMenuButton>
            </GenericDropdown>
          </OverflowMenuStyles>
        )}
        <Button
          label="Reschedule"
          category="danger"
          onClick={() => onRescheduledClick?.(appointment)}
        />
      </>
    );
  };

  if (isMobile || isTablet) {
    return (
      <Container>
        {appointments.map(item => {
          const { formatTime } = getTimeZone(item.timeZone, item.appointmentType);

          return (
            <LisItemMobile highlightTodaysListItems={highlightTodaysListItems} key={item.id}>
              {highlightTodaysListItems && <TodaysBadge label="Today!" size="md" type="default" />}
              <HeaderMobile>
                {shouldDisplayProvider(item) && item.provider?.name && (
                  <Initials name={item.provider?.name} size="md" />
                )}
                <div>
                  <Text tag="p" fontStyle="semibold" $multilineEllipses={2}>
                    {item.closureStatus === 'checked_out' && <CheckIcon />}
                    {item.closureStatus === 'no_show' && <QuestionIcon />}
                    {item.appointmentType.name}
                  </Text>
                  {shouldDisplayProvider(item) && item.provider?.name && (
                    <>
                      <Text>with</Text> <Text fontStyle="semibold">{item.provider?.name}</Text>
                    </>
                  )}
                </div>
              </HeaderMobile>
              <DateAndTime>
                <Date>
                  <Calendar type="regular" />{' '}
                  <Text tag="time" fontStyle={highlightTodaysListItems ? 'semibold' : 'regular'}>
                    {highlightTodaysListItems
                      ? 'Today'
                      : formatTime(item.startTime, DEFAULT_DATE_FORMAT)}
                  </Text>
                </Date>
                <Time>
                  <Clock type="regular" />
                  <Text tag="time" fontStyle={highlightTodaysListItems ? 'semibold' : 'regular'}>
                    {formatTime(item.startTime, HOUR_WITH_TIMEZONE)}
                  </Text>
                </Time>
              </DateAndTime>
              <ActionsContainer>{renderActions(item)}</ActionsContainer>
            </LisItemMobile>
          );
        })}
        {selectedAppointment && (
          <AppointmentDetailModal
            appointment={selectedAppointment}
            open={!!selectedAppointment}
            onClose={() => {
              setSelectedAppointment(null);
            }}
            onRescheduledClick={onRescheduledClick}
          />
        )}
      </Container>
    );
  }

  return (
    <Container>
      <ListHeader>
        <Text size="sm">Name</Text>
        <Text size="sm">Date and Time</Text>
        <Text size="sm">Location and Provider</Text>
      </ListHeader>
      {appointments.map(item => {
        const { formatTime } = getTimeZone(item.timeZone, item.appointmentType);

        return (
          <ListItem highlightTodaysListItems={highlightTodaysListItems} key={item.id}>
            <Text $multilineEllipses={2}>
              {item.closureStatus === 'checked_out' && <CheckIcon />}
              {item.closureStatus === 'no_show' && <QuestionIcon />}
              {highlightTodaysListItems && <TodaysBadge label="Today!" size="md" type="default" />}
              {item.appointmentType.name}
            </Text>
            <ListItemInfo>
              <Calendar type="regular" />
              <Text tag="time" fontStyle={highlightTodaysListItems ? 'semibold' : 'regular'}>
                {highlightTodaysListItems ? 'Today' : formatTime(item.startTime, 'ddd, MMM D')}
              </Text>
              <Clock type="regular" />
              <Text tag="time" fontStyle={highlightTodaysListItems ? 'semibold' : 'regular'}>
                {formatTime(item.startTime, 'hh:mm A z')}
              </Text>
            </ListItemInfo>
            <ListItemInfo>
              <Pin type="regular" />
              <Text $multilineEllipses={1}>{item.location.name}</Text>
              {shouldDisplayProvider(item) && item.provider?.name && (
                <>
                  <Initials name={item.provider?.name} />
                  <Text $multilineEllipses={1}>{item.provider?.name}</Text>
                </>
              )}
            </ListItemInfo>
            <ActionsContainer>{renderActions(item)}</ActionsContainer>
          </ListItem>
        );
      })}
      {selectedAppointment && (
        <AppointmentDetailModal
          appointment={selectedAppointment}
          open={!!selectedAppointment}
          onClose={() => {
            setSelectedAppointment(null);
          }}
          onRescheduledClick={onRescheduledClick}
        />
      )}
    </Container>
  );
};
